import { useSearchParams } from "react-router-dom";
import PageMeta from "@/components/seo/PageMeta";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import CollectionCard from "@/components/base/CollectionCard";
import { collections } from "@/mocks/collections";
import { categories, SITE_URL } from "@/mocks/site";

export default function Collections() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") ?? "all";
  const query = searchParams.get("q") ?? "";
  const trimmed = query.trim().toLowerCase();

  const updateParams = (next: { cat?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.cat !== undefined) {
      if (next.cat === "all") params.delete("cat");
      else params.set("cat", next.cat);
    }
    if (next.q !== undefined) {
      const value = next.q.trim();
      if (!value) params.delete("q");
      else params.set("q", value);
    }
    setSearchParams(params, { replace: true });
  };

  const filtered = collections.filter((item) => {
    const matchCat = activeCat === "all" || item.category === activeCat;
    if (!matchCat) return false;
    if (!trimmed) return true;
    const haystack = [
      item.title,
      item.summary,
      item.drama,
      item.tags.join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(trimmed);
  });

  const activeCategory = categories.find((item) => item.slug === activeCat);
  const heading =
    activeCat === "all"
      ? "全部合集"
      : activeCategory
      ? `${activeCategory.name} · 合集`
      : "全部合集";

  const collectionPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${heading} | 吃瓜片场`,
    url: `${SITE_URL}/collections`,
    inLanguage: "zh-CN",
    description:
      "私拍吃瓜花絮、路透、侧拍、彩蛋与候场片段合集列表，可按分类切换并搜索关键词。",
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: filtered.length,
      itemListElement: filtered.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.title,
        url: `${SITE_URL}/collections/${item.id}`,
      })),
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "合集大厅",
        item: `${SITE_URL}/collections`,
      },
    ],
  };

  return (
    <>
      <PageMeta
        title={`${heading} | 吃瓜片场私拍吃瓜合集`}
        description="按花絮、路透、侧拍、彩蛋、候场五个分类浏览私拍吃瓜合集，也可以直接搜索剧名、演员或道具关键词，快速定位想要的现场片段。"
        keywords="私拍吃瓜合集,花絮合集,路透合集,侧拍合集,彩蛋合集,候场合集"
        path="/collections"
        jsonLd={[collectionPageSchema, breadcrumbSchema]}
      />

      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <Breadcrumbs items={[{ label: "合集大厅" }]} />
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-5">
            合集大厅
          </h1>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mt-4 max-w-2xl">
            这里收录了站内全部私拍吃瓜合集，覆盖花絮现场、路透速览、侧拍角度、彩蛋收集与候场切片。
            先选分类，再用关键词缩小范围，比在时间线里翻找高效得多。
          </p>

          <div className="mt-8 flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => updateParams({ cat: "all" })}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  activeCat === "all"
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300"
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => updateParams({ cat: cat.slug })}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1.5 ${
                    activeCat === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  <i className={cat.icon} aria-hidden="true" />
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="relative w-full sm:max-w-md">
                <i
                  className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-base"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => updateParams({ q: event.target.value })}
                  placeholder="搜索剧名、关键词或道具…"
                  aria-label="搜索合集"
                  className="w-full pl-11 pr-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none transition-colors"
                />
              </div>
              <p className="text-sm text-foreground-500">
                共 <span className="text-primary-700 font-medium">{filtered.length}</span> 篇合集
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-7xl mx-auto">
          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filtered.map((item) => (
                <CollectionCard key={item.id} collection={item} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-2xl text-foreground-400">
                <i className="ri-search-eye-line" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-900 mt-5">
                没有找到匹配的合集
              </h2>
              <p className="text-sm text-foreground-600 mt-2 max-w-md">
                试着更换关键词，或切换到其他分类看看。也可以直接浏览全部合集。
              </p>
              <button
                type="button"
                onClick={() => updateParams({ cat: "all", q: "" })}
                className="mt-6 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                重置筛选
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}